import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  Package,
  MessageSquare,
  Phone,
  MapPin,
  Copy,
  Check,
  Loader2,
  Bell,
  Download,
  AlertTriangle,
  ChevronDown,
  RotateCcw,
  User,
  StickyNote,
  CircleDot,
} from 'lucide-react';
import clsx from 'clsx';
import { getImageUrl } from '../../lib/supabase';
import { formatPrice } from '../../lib/utils';
import {
  useOrderHistory,
  useUpdateOrderStatus,
  useAddOrderNote,
  useMarkNotified,
} from '../../services/adminService';

const overlay = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const panel = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } },
  exit: { x: '100%', transition: { duration: 0.2 } },
};

/* ── Status configuration ── */

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200', dotColor: 'bg-amber-500', ringColor: 'ring-amber-200', icon: Clock, step: 0 },
  confirmed: { label: 'Confirmed', color: 'bg-blue-50 text-blue-700 border-blue-200', dotColor: 'bg-blue-500', ringColor: 'ring-blue-200', icon: CheckCircle2, step: 1 },
  in_progress: { label: 'In Progress', color: 'bg-purple-50 text-purple-700 border-purple-200', dotColor: 'bg-purple-500', ringColor: 'ring-purple-200', icon: Truck, step: 2 },
  delivered: { label: 'Delivered', color: 'bg-green-50 text-green-700 border-green-200', dotColor: 'bg-green-500', ringColor: 'ring-green-200', icon: CheckCircle2, step: 3 },
  completed: { label: 'Completed', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', dotColor: 'bg-emerald-600', ringColor: 'ring-emerald-200', icon: Check, step: 4 },
  cancelled: { label: 'Cancelled', color: 'bg-red-50 text-error border-red-200', dotColor: 'bg-red-500', ringColor: 'ring-red-200', icon: XCircle, step: -1 },
};

/** The normal happy-path flow */
const STATUS_FLOW = ['pending', 'confirmed', 'in_progress', 'delivered', 'completed'];

const STATUS_TEMPLATES = {
  confirmed: 'Order confirmed — shipping ETA 2 days.',
  in_progress: 'Order is being prepared and will be dispatched shortly.',
  delivered: 'Order has been delivered successfully.',
  completed: 'Order completed. Thank you for your purchase!',
  cancelled: 'Order has been cancelled. Refund will be processed if applicable.',
};

/**
 * Build the WhatsApp message for an order.
 */
function buildWhatsAppMessage(order) {
  const product = order.product_snapshot?.title || order.products?.title || 'Product';
  const price = order.product_snapshot?.price || order.products?.price || order.total_price;
  return `Hi ${order.customer_name}! Your order for *${product}* (${formatPrice(price)}) × ${order.quantity || 1} is confirmed.\n\nWe'll notify you when it's ready. Thank you!`;
}

/**
 * Format a relative time string.
 */
function relativeTime(date) {
  const now = new Date();
  const diff = now - new Date(date);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/* ── Timeline entry animation ── */
const timelineItem = {
  hidden: { opacity: 0, x: -12 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.08, type: 'spring', damping: 20, stiffness: 200 },
  }),
};

/**
 * Order details slide-over panel with full status workflow,
 * vertical timeline audit trail, and admin actions.
 */
export function OrderDetailPane({ order, onClose }) {
  const { data: history } = useOrderHistory(order?.id);
  const statusMutation = useUpdateOrderStatus();
  const noteMutation = useAddOrderNote();
  const notifyMutation = useMarkNotified();

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [statusNote, setStatusNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset state when order changes
  useEffect(() => {
    if (order) {
      setStatusDropdownOpen(false);
      setStatusConfirm(null);
      setStatusNote('');
      setInternalNote(order.internal_notes || '');
      setShowNoteInput(false);
      setCopied(false);
    }
  }, [order]);

  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Escape') onClose(); },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Status workflow
  const handleStatusSelect = (newStatus) => {
    setStatusDropdownOpen(false);
    setStatusConfirm({ status: newStatus });
    setStatusNote(STATUS_TEMPLATES[newStatus] || '');
  };

  const confirmStatusChange = () => {
    if (!statusConfirm) return;
    statusMutation.mutate(
      { orderId: order.id, newStatus: statusConfirm.status, note: statusNote || null },
      { onSuccess: () => setStatusConfirm(null) }
    );
  };

  const handleSaveNote = () => {
    noteMutation.mutate(
      { orderId: order.id, note: internalNote },
      { onSuccess: () => setShowNoteInput(false) }
    );
  };

  const handleCopyWhatsApp = () => {
    const msg = buildWhatsAppMessage(order);
    navigator.clipboard.writeText(msg).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleExportCSV = () => {
    if (!order) return;
    const headers = ['Order ID', 'Customer', 'Phone', 'Address', 'Product', 'Qty', 'Total', 'Status', 'Date'];
    const product = order.product_snapshot?.title || order.products?.title || '';
    const total = order.total_price || (order.products?.price || 0) * (order.quantity || 1);
    const row = [
      order.id, order.customer_name, order.customer_phone, order.customer_address,
      product, order.quantity || 1, total, order.status,
      new Date(order.created_at).toLocaleDateString(),
    ];
    const csv = [headers.join(','), row.map((v) => `"${v}"`).join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-${order.id.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!order) return null;

  const currentStatus = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
  const CurrentIcon = currentStatus.icon;
  const productImage = getImageUrl(order.product_snapshot?.image_path || order.products?.image_path);
  const productTitle = order.product_snapshot?.title || order.products?.title || 'Product';
  const productPrice = order.product_snapshot?.price || order.products?.price;
  const orderTotal = order.total_price || (productPrice || 0) * (order.quantity || 1);
  const whatsappUrl = order.whatsapp_link || `https://wa.me/${order.customer_phone?.replace(/\D/g, '')}`;

  // Build full timeline: order created + history entries
  const timeline = [
    {
      id: 'created',
      type: 'created',
      new_status: 'pending',
      note: null,
      created_at: order.created_at,
    },
    ...(history || []).map((h) => ({ ...h, type: 'status_change' })),
  ];

  // Determine which statuses are "next" in the flow for highlighting
  const currentStep = currentStatus.step;
  const nextInFlow = STATUS_FLOW.find((s) => (STATUS_CONFIG[s]?.step ?? -1) > currentStep);

  return (
    <AnimatePresence>
      {order && (
        <>
          <motion.div
            key="order-backdrop"
            variants={overlay}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="fixed inset-0 z-40 bg-brand-dark/40 backdrop-blur-sm"
          />

          <motion.div
            key="order-panel"
            variants={panel}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-brand-white shadow-2xl flex flex-col"
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border">
              <div>
                <h2 className="text-lg font-extrabold text-brand-dark">Order Details</h2>
                <p className="text-xs text-brand-gray mt-0.5 font-mono">#{order.id.slice(0, 8)}</p>
              </div>
              <div className="flex items-center gap-2">
                {order.is_duplicate && (
                  <span className="flex items-center gap-1 text-[10px] font-bold text-warning bg-warning/10 px-2 py-1 rounded-full">
                    <AlertTriangle size={10} />
                    Possible duplicate
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* ── Scrollable content ── */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">

              {/* ── Status badge + stepper ── */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className={clsx(
                    'inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-xl border',
                    currentStatus.color
                  )}>
                    <CurrentIcon size={14} />
                    {currentStatus.label}
                  </span>
                  <span className="text-xs text-brand-gray">
                    {new Date(order.created_at).toLocaleString()}
                  </span>
                </div>

                {/* Horizontal progress stepper (happy path) */}
                {order.status !== 'cancelled' && (
                  <div className="flex items-center gap-1">
                    {STATUS_FLOW.map((key, i) => {
                      const cfg = STATUS_CONFIG[key];
                      const step = cfg.step;
                      const isReached = currentStep >= step;
                      const isCurrent = order.status === key;
                      return (
                        <div key={key} className="flex items-center gap-1 flex-1">
                          <div className={clsx(
                            'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
                            isCurrent && `${cfg.dotColor} ring-4 ${cfg.ringColor}`,
                            isReached && !isCurrent && cfg.dotColor,
                            !isReached && 'bg-gray-200'
                          )}>
                            {isReached ? (
                              <Check size={12} className="text-brand-white" />
                            ) : (
                              <span className="text-[8px] font-bold text-brand-gray">{i + 1}</span>
                            )}
                          </div>
                          {i < STATUS_FLOW.length - 1 && (
                            <div className={clsx(
                              'flex-1 h-0.5 rounded-full transition-colors',
                              currentStep > step ? cfg.dotColor : 'bg-gray-200'
                            )} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {order.status !== 'cancelled' && (
                  <div className="flex items-center justify-between px-1">
                    {STATUS_FLOW.map((key) => (
                      <span key={key} className="text-[9px] font-bold text-brand-gray text-center flex-1 truncate">
                        {STATUS_CONFIG[key].label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Change status dropdown ── */}
              <div className="space-y-3">
                <p className="text-xs font-bold text-brand-gray uppercase tracking-wide">Change Status</p>

                <AnimatePresence mode="wait">
                  {statusConfirm ? (
                    <motion.div
                      key="confirm"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="p-4 bg-brand-light border border-ui-border rounded-xl space-y-3"
                    >
                      <div className="flex items-center gap-2">
                        {order.status !== statusConfirm.status && (
                          <>
                            <span className={clsx(
                              'inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg border',
                              currentStatus.color
                            )}>
                              {currentStatus.label}
                            </span>
                            <span className="text-brand-gray">→</span>
                          </>
                        )}
                        <span className={clsx(
                          'inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg border',
                          STATUS_CONFIG[statusConfirm.status]?.color
                        )}>
                          {STATUS_CONFIG[statusConfirm.status]?.label}
                        </span>
                      </div>

                      <textarea
                        rows={2}
                        value={statusNote}
                        onChange={(e) => setStatusNote(e.target.value)}
                        placeholder="Add a note (optional)..."
                        className="w-full px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none placeholder:text-ui-muted"
                      />

                      {statusNote && (
                        <div className="p-2 bg-brand-white border border-ui-border rounded-lg">
                          <p className="text-[10px] font-bold text-brand-gray mb-1">Message preview</p>
                          <p className="text-xs text-brand-dark leading-relaxed">{statusNote}</p>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={confirmStatusChange}
                          disabled={statusMutation.isPending}
                          className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-brand-red hover:bg-red-700 text-brand-white rounded-xl transition-colors"
                        >
                          {statusMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                          Confirm Change
                        </button>
                        <button
                          onClick={() => setStatusConfirm(null)}
                          className="px-4 py-2 text-sm font-bold text-brand-gray hover:bg-gray-200 rounded-xl transition-colors"
                        >
                          Cancel
                        </button>
                      </div>

                      {statusMutation.isError && (
                        <p className="text-xs text-error">
                          {statusMutation.error?.message || 'Failed to update status'}
                        </p>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      key="dropdown"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="relative"
                    >
                      <button
                        onClick={() => setStatusDropdownOpen((v) => !v)}
                        className="w-full flex items-center justify-between px-4 py-2.5 bg-brand-white border border-ui-border rounded-xl text-sm font-bold text-brand-dark hover:bg-brand-light transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <CurrentIcon size={14} className={clsx(
                            order.status === 'cancelled' ? 'text-error' : 'text-brand-gray'
                          )} />
                          Current: {currentStatus.label}
                        </span>
                        <ChevronDown size={16} className={clsx(
                          'text-brand-gray transition-transform',
                          statusDropdownOpen && 'rotate-180'
                        )} />
                      </button>

                      <AnimatePresence>
                        {statusDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -4, scaleY: 0.95 }}
                            animate={{ opacity: 1, y: 0, scaleY: 1 }}
                            exit={{ opacity: 0, y: -4, scaleY: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full left-0 right-0 mt-1 bg-brand-white border border-ui-border rounded-xl shadow-lg overflow-hidden z-10 origin-top"
                          >
                            {/* Suggested next status */}
                            {nextInFlow && order.status !== 'completed' && (
                              <div className="px-3 py-2 border-b border-ui-border bg-brand-light/50">
                                <p className="text-[10px] font-bold text-brand-gray uppercase tracking-wide mb-1">
                                  Suggested next
                                </p>
                                <button
                                  onClick={() => handleStatusSelect(nextInFlow)}
                                  className={clsx(
                                    'w-full flex items-center gap-2 px-3 py-2 text-sm font-bold rounded-lg transition-colors',
                                    STATUS_CONFIG[nextInFlow].color
                                  )}
                                >
                                  {(() => { const Icon = STATUS_CONFIG[nextInFlow].icon; return <Icon size={14} />; })()}
                                  {STATUS_CONFIG[nextInFlow].label}
                                </button>
                              </div>
                            )}

                            <div className="py-1">
                              <p className="px-3 py-1 text-[10px] font-bold text-brand-gray uppercase tracking-wide">
                                All statuses
                              </p>
                              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                                const Icon = cfg.icon;
                                const isCurrent = key === order.status;
                                const isRollback = cfg.step < currentStep && cfg.step >= 0;
                                return (
                                  <button
                                    key={key}
                                    onClick={() => !isCurrent && handleStatusSelect(key)}
                                    disabled={isCurrent}
                                    className={clsx(
                                      'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left',
                                      isCurrent
                                        ? 'bg-brand-light font-bold text-brand-dark cursor-default'
                                        : 'text-brand-dark hover:bg-brand-light/70 font-medium'
                                    )}
                                  >
                                    <div className={clsx(
                                      'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0',
                                      isCurrent ? cfg.dotColor : 'bg-gray-200'
                                    )}>
                                      <Icon size={10} className={isCurrent ? 'text-brand-white' : 'text-brand-gray'} />
                                    </div>
                                    <span className="flex-1">{cfg.label}</span>
                                    {isCurrent && (
                                      <span className="text-[10px] font-bold text-brand-gray bg-brand-light px-1.5 py-0.5 rounded">
                                        Current
                                      </span>
                                    )}
                                    {isRollback && !isCurrent && (
                                      <span className="flex items-center gap-0.5 text-[10px] font-bold text-warning">
                                        <RotateCcw size={8} />
                                        Rollback
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* ── Product snapshot ── */}
              <div className="bg-brand-light/50 border border-ui-border rounded-xl p-4">
                <p className="text-xs font-bold text-brand-gray uppercase tracking-wide mb-3">Product</p>
                <div className="flex items-center gap-3">
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={productTitle}
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0 bg-brand-light"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0">
                      <Package size={20} className="text-ui-muted" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-brand-dark truncate">{productTitle}</h4>
                    <p className="text-xs text-brand-gray mt-0.5">
                      {formatPrice(productPrice)} × {order.quantity || 1}
                    </p>
                  </div>
                  <p className="text-sm font-extrabold text-brand-dark">{formatPrice(orderTotal)}</p>
                </div>
                {!order.product_snapshot?.title && !order.products?.title && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-warning font-medium">
                    <AlertTriangle size={12} />
                    Product snapshot missing — showing fallback data
                  </div>
                )}
              </div>

              {/* ── Customer info ── */}
              <div className="space-y-3">
                <p className="text-xs font-bold text-brand-gray uppercase tracking-wide">Customer</p>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center gap-3 p-3 bg-brand-light/50 border border-ui-border rounded-xl">
                    <User size={16} className="text-brand-gray flex-shrink-0" />
                    <div>
                      <p className="text-xs text-brand-gray">Name</p>
                      <p className="text-sm font-bold text-brand-dark">{order.customer_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-brand-light/50 border border-ui-border rounded-xl">
                    <Phone size={16} className="text-brand-gray flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-brand-gray">Phone</p>
                      <p className="text-sm font-bold text-brand-dark">{order.customer_phone}</p>
                    </div>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-success hover:underline"
                    >
                      WhatsApp
                    </a>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-brand-light/50 border border-ui-border rounded-xl">
                    <MapPin size={16} className="text-brand-gray flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-brand-gray">Address</p>
                      <p className="text-sm font-bold text-brand-dark">{order.customer_address}</p>
                    </div>
                  </div>
                </div>
                {order.note && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs font-bold text-amber-700 mb-1">Customer Note</p>
                    <p className="text-sm text-brand-dark">{order.note}</p>
                  </div>
                )}
              </div>

              {/* ── WhatsApp message ── */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-brand-gray uppercase tracking-wide">WhatsApp Message</p>
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm text-brand-dark whitespace-pre-line leading-relaxed">
                    {buildWhatsAppMessage(order)}
                  </p>
                  <button
                    onClick={handleCopyWhatsApp}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-success hover:underline"
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? 'Copied!' : 'Copy message'}
                  </button>
                </div>
              </div>

              {/* ── Internal note ── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-brand-gray uppercase tracking-wide">Internal Note</p>
                  {!showNoteInput && (
                    <button
                      onClick={() => setShowNoteInput(true)}
                      className="text-xs font-bold text-brand-red hover:underline"
                    >
                      {order.internal_notes ? 'Edit' : 'Add'}
                    </button>
                  )}
                </div>
                {showNoteInput ? (
                  <div className="space-y-2">
                    <textarea
                      rows={3}
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      placeholder="Internal staff notes..."
                      className="w-full px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none placeholder:text-ui-muted"
                    />
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSaveNote}
                        disabled={noteMutation.isPending}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold bg-brand-red hover:bg-red-700 text-brand-white rounded-xl transition-colors"
                      >
                        {noteMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                        Save Note
                      </button>
                      <button
                        onClick={() => { setShowNoteInput(false); setInternalNote(order.internal_notes || ''); }}
                        className="px-4 py-2 text-sm font-bold text-brand-gray hover:bg-gray-200 rounded-xl transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : order.internal_notes ? (
                  <div className="p-3 bg-brand-light/50 border border-ui-border rounded-xl flex items-start gap-2">
                    <StickyNote size={14} className="text-brand-gray flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-brand-dark whitespace-pre-line">{order.internal_notes}</p>
                  </div>
                ) : (
                  <p className="text-xs text-ui-muted italic">No internal notes</p>
                )}
              </div>

              {/* ═══════════════════════════════════════
               * VERTICAL TIMELINE — Audit Trail
               * ═══════════════════════════════════════ */}
              <div className="space-y-3">
                <p className="text-xs font-bold text-brand-gray uppercase tracking-wide">
                  Order Timeline
                </p>

                <div className="relative pl-6">
                  {/* Vertical line */}
                  <div className="absolute left-[9px] top-2 bottom-2 w-0.5 bg-ui-border" />

                  <div className="space-y-0">
                    {timeline.map((entry, i) => {
                      const isCreated = entry.type === 'created';
                      const statusKey = entry.new_status;
                      const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.pending;
                      const Icon = cfg.icon;
                      const isLast = i === timeline.length - 1;
                      const isRollback =
                        !isCreated &&
                        entry.old_status &&
                        (STATUS_CONFIG[entry.old_status]?.step ?? 0) > (cfg.step ?? 0) &&
                        cfg.step >= 0;

                      return (
                        <motion.div
                          key={entry.id}
                          custom={i}
                          variants={timelineItem}
                          initial="hidden"
                          animate="visible"
                          className="relative pb-5 last:pb-0"
                        >
                          {/* Dot on the timeline */}
                          <div className={clsx(
                            'absolute -left-6 top-0.5 w-[18px] h-[18px] rounded-full border-2 border-brand-white flex items-center justify-center z-10',
                            isLast ? `${cfg.dotColor} ring-4 ${cfg.ringColor}` : cfg.dotColor
                          )}>
                            <Icon size={8} className="text-brand-white" />
                          </div>

                          {/* Content */}
                          <div className={clsx(
                            'ml-2 p-3 rounded-xl transition-colors',
                            isLast
                              ? 'bg-brand-light/70 border border-ui-border'
                              : 'hover:bg-brand-light/30'
                          )}>
                            {/* Title */}
                            <div className="flex items-center gap-2">
                              {isCreated ? (
                                <p className="text-sm font-bold text-brand-dark">Order Created</p>
                              ) : (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={clsx(
                                    'inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border',
                                    STATUS_CONFIG[entry.old_status]?.color || 'bg-gray-50 text-brand-gray border-gray-200'
                                  )}>
                                    {STATUS_CONFIG[entry.old_status]?.label || entry.old_status}
                                  </span>
                                  <span className="text-xs text-brand-gray">→</span>
                                  <span className={clsx(
                                    'inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border',
                                    cfg.color
                                  )}>
                                    <Icon size={8} />
                                    {cfg.label}
                                  </span>
                                  {isRollback && (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-warning bg-warning/10 px-1.5 py-0.5 rounded">
                                      <RotateCcw size={8} />
                                      Rollback
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Note */}
                            {entry.note && (
                              <p className="text-xs text-brand-dark mt-1.5 leading-relaxed bg-brand-white/50 px-2.5 py-1.5 rounded-lg border border-ui-border/50">
                                {entry.note}
                              </p>
                            )}

                            {/* Metadata: timestamp + who */}
                            <div className="flex items-center gap-3 mt-1.5">
                              <span className="text-[10px] text-ui-muted flex items-center gap-1">
                                <Clock size={9} />
                                {new Date(entry.created_at).toLocaleString()}
                              </span>
                              <span className="text-[10px] text-ui-muted">
                                {relativeTime(entry.created_at)}
                              </span>
                              {entry.changed_by && (
                                <span className="text-[10px] text-ui-muted flex items-center gap-1">
                                  <User size={9} />
                                  Admin
                                </span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Footer actions ── */}
            <div className="px-6 py-4 border-t border-ui-border flex items-center gap-3 bg-brand-white">
              {!order.notified_owner ? (
                <button
                  onClick={() => notifyMutation.mutate(order.id)}
                  disabled={notifyMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-brand-dark bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  <Bell size={14} />
                  {notifyMutation.isPending ? 'Marking...' : 'Mark Notified'}
                </button>
              ) : (
                <span className="flex items-center gap-1.5 text-xs font-bold text-success">
                  <Check size={14} />
                  Owner notified
                </span>
              )}
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-brand-dark bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
              >
                <Download size={14} />
                Export CSV
              </button>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-brand-white bg-success hover:bg-green-700 rounded-xl transition-colors ml-auto"
              >
                <MessageSquare size={14} />
                WhatsApp
              </a>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
